import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import authService from '../../services/authService';
import { Button } from '../common/index.js';
import logo from "../../static/images/logo.png";

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = sessionStorage.getItem('resetEmail');
    if (savedEmail) setEmail(savedEmail);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(
        email,
        otp,
        newPassword,
        confirmPassword
      );

      setMessage(response.message || 'Password reset successfully');

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light flex items-center justify-center px-4 relative">

      <Link to="/" className="absolute top-6 left-6">
        <Button variant="primary" size="sm" icon={ArrowLeft}>
          Back to Home
        </Button>
      </Link>

      <div className="w-full max-w-5xl bg-white rounded-card shadow-equal grid grid-cols-1 lg:grid-cols-2">

        <div className="hidden lg:flex items-center justify-center bg-brand-light text-secondary rounded-l-card">
          <div className="text-center px-12">
            <img
              src={logo}
              alt="Metro Logistics"
              className="mx-auto mb-8 max-w-[160px]"
            />
            <h2 className="text-2xl font-semibold text-brand">
              Reset your password securely
            </h2>
            <p className="mt-4 text-secondary text-sm">
              Protect your account with a new strong password
            </p>
          </div>
        </div>

        <div className="p-8 sm:p-12 sm:py-8">

          <h1 className="text-2xl font-semibold text-brand">
            Reset Password
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enter OTP and choose a new password
          </p>

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 text-danger px-4 py-3 rounded-card text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="mt-6 bg-green-50 border border-green-200 text-success px-4 py-2 rounded-card text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">

            <div>
              <label className="text-sm font-medium text-secondary">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-clean mt-2"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-secondary">
                OTP Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input-clean mt-2 text-center tracking-widest font-semibold"
                placeholder="000000"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-secondary">
                New Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-clean pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm hover:text-secondary"
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-secondary">
                Confirm Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-clean pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm hover:text-secondary"
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              Reset Password
            </Button>
          </form>

          <div className="mt-6 text-sm text-center">
            <Link to="/login" className="text-brand hover:underline">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-secondary">
        © 2026 Metro Logistics. All rights reserved.
      </div>

    </div>
  );
};

export default ResetPassword;
